import { defineStore } from 'pinia';
import { computed } from 'vue';
import { last } from 'lodash-es';
import { sortQuotes } from '@/common/utils/sorters';
import sortStoragePoliciesByName from '@/common/utils/sorters/policySorters';
import useCommonStore from '@/common/store/pinia/common';
import { StoragePolicy as StoragePolicySchema } from '@schema';
import { DEFAULT_POLICY_NAME } from '@/common/constants/policy.constants';
import computeStoragePolicyMapper, { computeStoragePolicyModel as PolicyModel } from '@/common/store/pinia/computeStoragePolicies/computeStoragePolicyMapper';

const useComputeStoragePoliciesStore = defineStore('computeStoragePolicies', () => {
  const commonStore = useCommonStore<StoragePolicySchema, PolicyModel>({
    getUrl: () => '/api/v2/storage_policies',
    mapItem: computeStoragePolicyMapper,
    updateMethod: 'put',
  })();

  const { getItemByProp, getItems, getItemsByProp } = commonStore;

  const getAvailablePolicies = computed((): PolicyModel[] => getItemsByProp.value('isAvailable')(true));

  const getPoliciesSortedByQuotes = computed<{ quota: Quota; name: string }[]>(() => getAvailablePolicies.value
      .map(p => ({ quota: p.quota, name: p.name }))
      .filter((p): p is { quota: Quota; name: string } => p.quota !== null)
      .sort(sortQuotes)
  );

  const getDefaultAvailablePolicy = computed(() => {
    const defaultPolicy = getItemByProp.value('name')(DEFAULT_POLICY_NAME);

    if (defaultPolicy?.isAvailable) {
      return defaultPolicy;
    }

    return last(getPoliciesSortedByQuotes.value);
  });

  const getAvailablePoliciesSortedByName = computed((): PolicyModel[] => getAvailablePolicies.value.sort(sortStoragePoliciesByName));

  const getPoliciesSortedByName = computed((): PolicyModel[] => getItems.value.sort(sortStoragePoliciesByName));

  return {
    ...commonStore,
    getAvailablePolicies,
    getPoliciesSortedByQuotes,
    getDefaultAvailablePolicy,
    getAvailablePoliciesSortedByName,
    getPoliciesSortedByName,
  };
});

export default useComputeStoragePoliciesStore;
